import React, { useContext } from 'react';
import {
  oneOf,
  bool as boolType,
  any,
  shape as shapeType,
  string as stringType,
  number as numberType,
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  params
} from '@thd-nucleus/data-sources';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import {
  Col, Row, Image, Carousel, Heading
} from '@thd-olt-component-react/core-ui';
import classNames from 'classnames/bind';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { CarouselHeader } from './CarouselHeader';
import { RequiredAttributes } from './RequiredAttributes';
import { getSkippers, isAppliance } from '../utils/helpers';
import '../../styles/thd-recs-containers.scss';
import styles from './RecommendationsProductPodCarousel.module.scss';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const cx = classNames.bind(styles);
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({}, ProductPod);
const RecommendationsProductPodCarousel = (props) => {
  const { channel } = useContext(ExperienceContext);

  const isMobile = channel === 'mobile';

  const {
    anchorImage,
    data,
    hideTitle,
    loading,
    scheme,
    showLoading,
    storeId,

    // productPodProps,
    brandTitleMaxLine,
    customEventObject,
    hideATC,
    hideBulkPrice,
    hideFavorites,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hideRating,
    hideRebates,
    hideSavingsText,
    hideShippingInfo,
    hideShopThisCollection,
    showSubscribeAndSave,
    zipCode,
    slidesPer,
    keepAddToCart,
    noATCFulfillment
  } = props;

  let hideAddToCart;
  if (keepAddToCart === true) {
    hideAddToCart = false; // show ATC always - including MW
  } else if (hideATC || isMobile) {
    hideAddToCart = true;
  }

  const defaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    ...getSkippers({ skipSubscribeAndSave: !showSubscribeAndSave })
  };

  const { products, metadata } = data || {};
  let title = metadata?.title ? metadata.title : metadata?.batteryName;

  let breakpoints = ({});
  if (!slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      md: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      lg: {
        slidesPerView: metadata?.batteryImageUrl || anchorImage ? 4 : 6,
        slidesPerGroup: metadata?.batteryImageUrl || anchorImage ? 4 : 6
      },
      xl: {
        slidesPerView: metadata?.batteryImageUrl || anchorImage ? 4 : 6,
        slidesPerGroup: metadata?.batteryImageUrl || anchorImage ? 4 : 6
      }
    });
  } else if (slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      md: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      lg: {
        slidesPerView: metadata?.batteryImageUrl || anchorImage ? 1 : slidesPer,
        slidesPerGroup: metadata?.batteryImageUrl || anchorImage ? 1 : slidesPer
      },
      xl: {
        slidesPerView: metadata?.batteryImageUrl || anchorImage ? 1 : slidesPer,
        slidesPerGroup: metadata?.batteryImageUrl || anchorImage ? 1 : slidesPer
      }
    });
  }

  return (
    <Wrapper>
      {showLoading && loading && (
        <LoadingPlaceholder hideTitle={hideTitle} />
      )}
      {products?.length > 0 && (
        <>
          {!hideTitle && (
            <CarouselHeader title={title} />
          )}
          {anchorImage && !isMobile && (
            <Col xs="4" className={cx('carouselAnchorImage')}>
              <Image
                src={anchorImage}
                alt="main-product-image"
                height="400"
                width="400"
                data-testid="anchor-image"
              />
              <div className={cx('carouselAnchorImage__currentItem__label')}>Current Item</div>
            </Col>
          )}
          {metadata?.batteryImageUrl && (
            <Col xs="4" className={cx('carouselAnchorImage')}>
              <div className="battery-systems__feature--img">
                <Image
                  src={metadata?.batteryImageUrl}
                  alt="main-product-image"
                  height="300"
                  width="300"
                  data-testid="anchor-image"
                />
              </div>
              <div className="battery-systems__feature--title">{metadata?.batteryTitle}</div>
              <div className="battery-systems__feature--list">
                {/* eslint-disable-next-line react/no-danger */}
                <ul dangerouslySetInnerHTML={{ __html: metadata?.batteryDesc }} />
              </div>
              <div className="battery-systems__shop--link">
                <a
                  href={metadata?.batteryPlpUrl}
                  rel="noreferrer"
                  target={isMobile ? '_self' : '_blank'}
                  data-testid="tooltip-product"
                > Shop {metadata?.batteryName}
                </a>
              </div>
            </Col>
          )}

          <Col xs={metadata?.batteryImageUrl || anchorImage ? '8' : '12'}>
            <QueryProvider
              dataSource={scheme}
              cacheKey={`${scheme}-recommendations-carousel`}
              defaultVariables={defaultVariables}
            >
              <Carousel
                breakpoints={breakpoints}
              >
                {data?.products.map((dynamicProduct, idx) => {
                  const { product } = dynamicProduct;
                  const { itemId } = product;

                  return (
                    <ProductPod
                      brandTitleMaxLine={brandTitleMaxLine}
                      customEventObject={customEventObject}
                      hideATC={hideAddToCart}
                      hideBulkPrice={hideBulkPrice}
                      hideFavorites={hideFavorites}
                      hideFulfillment={hideFulfillment}
                      hideLimitPerOrder={hideLimitPerOrder}
                      hidePriceBadge={hidePriceBadge}
                      hideRating={hideRating}
                      hideRebates={hideRebates}
                      hideSavingsText={hideSavingsText}
                      hideShippingInfo={hideShippingInfo}
                      hideShopThisCollection={hideShopThisCollection}
                      itemId={itemId}
                      key={itemId}
                      position={idx}
                      showSubscribeAndSave={showSubscribeAndSave}
                      storeId={storeId}
                      noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                    />
                  );
                }
                )}
              </Carousel>
            </QueryProvider>
          </Col>
        </>
      )}
    </Wrapper>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string(),
}, ProductPod.dataModel.product);

export const wrappedProduct = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  // itemId: string(),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string(),
  })
});

RecommendationsProductPodCarousel.dataModel = extend(Wrapper, wrappedProduct);

RecommendationsProductPodCarousel.displayName = 'RecommendationsProductPodCarousel';

RecommendationsProductPodCarousel.propTypes = {
  ...ProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,

  // productPodProps
  // product-pod take only these values for brandTitleMaxLine prop
  brandTitleMaxLine: oneOf([2, 3, null]),
  customEventObject: shapeType({
    component: stringType,
    customEvent: stringType
  }),
  hideATC: boolType,
  hideBulkPrice: boolType,
  hideFavorites: boolType,
  hideFulfillment: boolType,
  hideLimitPerOrder: boolType,
  hidePriceBadge: boolType,
  hideRating: boolType,
  hideRebates: boolType,
  hideSavingsText: boolType,
  hideShopThisCollection: boolType,
  hideShippingInfo: boolType,
  showSubscribeAndSave: boolType,
  keepAddToCart: boolType,
  noATCFulfillment: boolType
};

RecommendationsProductPodCarousel.defaultProps = {
  hideTitle: false,
  scheme: 'drecs',
  showLoading: true,
  loading: false,
  data: null,

  brandTitleMaxLine: null,
  customEventObject: null,
  hideATC: false,
  hideBulkPrice: false,
  hideFavorites: true,
  hideFulfillment: true,
  hideLimitPerOrder: true,
  hidePriceBadge: true,
  hideRating: false,
  hideRebates: true,
  hideSavingsText: true,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  showSubscribeAndSave: false,
  keepAddToCart: null,
  noATCFulfillment: true
};

export { RecommendationsProductPodCarousel };
